/**
 * Monetary calculation utilities that operate in integer cents to avoid
 * IEEE 754 floating-point precision loss at .XX5 rounding boundaries.
 */

/** Rounds a dollar amount to 2 decimal places via integer cents. */
export function roundMoney(dollars: number): number {
  return Math.round(dollars * 100) / 100;
}

/**
 * Prorates a monetary total across a new quantity, rounding in cents.
 * Returns 0 when originalQuantity is zero or negative.
 *
 * Example: prorateAmount(100.10, 4, 3) = 75.08
 * The naive formula (100.10/4*3) gives 75.07 due to IEEE 754 loss.
 */
export function prorateAmount(
  originalAmount: number,
  originalQuantity: number,
  newQuantity: number,
): number {
  if (originalQuantity <= 0) return 0;
  const originalCents = Math.round(originalAmount * 100);
  return Math.round((originalCents * newQuantity) / originalQuantity) / 100;
}

/** Parses a string to a 2-decimal monetary amount. Returns 0 for invalid input. */
export function parseMoney(value: string): number {
  const num = Number.parseFloat(value.replaceAll(/[^0-9.]/g, ''));
  return Number.isNaN(num) ? 0 : roundMoney(num);
}

/** Formats a dollar amount as a plain 2-decimal string for an editable money field. */
export function formatMoneyInput(dollars: number): string {
  return roundMoney(dollars).toFixed(2);
}

/**
 * Parses an editable money field to a number, or null when it is empty or unparseable.
 * Distinct from parseMoney, which collapses invalid input to 0 — a form field needs
 * "nothing entered" to stay distinguishable from "zero entered".
 */
export function parseMoneyInput(raw: string | number | null | undefined): number | null {
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }
  const value = typeof raw === 'number' ? raw : Number.parseFloat(raw.replaceAll(',', ''));
  return Number.isFinite(value) ? value : null;
}

/**
 * Formats a dollar amount as en-US currency, or an em dash when there is no amount.
 * Kept here rather than duplicated per component: the Payments card and the Send Statement
 * modal must render the same figures identically.
 */
export function formatUsd(amount: number | undefined | null): string {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}
